import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import { t } from "@/lib/i18n/interpolate";
import {
  apaInTextAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  familyName,
  mlaInTextAuthors,
  naturalName,
  type PersonName,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  get,
  italic,
  mainTitle,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  parsePeople,
  publisherName,
  type SpecialValues,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A source you read about in another source — "as cited in", "qtd. in", "quoted in",
 * "cited in" — in APA 7, MLA 9, Chicago 18 and Harvard (Cite Them Right).
 *
 * Sources checked (October 2026):
 * - APA 7 §8.6, "Secondary sources" (apastyle.apa.org, updated July 2022): only the
 *   secondary source goes in the reference list; in the text, name the primary
 *   source and write "as cited in" the secondary source, with the primary's year if
 *   known — "(Rabbitt, 1982, as cited in Lyon et al., 2014)", "Allport's diary (as
 *   cited in Nicholson, 2003)"; a page refers to the source you read.
 * - MLA 9 §6.77 and the MLA Style Center, "Citing indirect sources" (September 20,
 *   2023): "qtd. in" before the source you consulted; the original author may be named
 *   in the prose or in the parenthetical, with no comma — "(Alexander Pope qtd. in
 *   Damrosch 239)"; only the source you consulted is in the works-cited list.
 * - Chicago 18 §14.160 (CMOS Q&A): "quoted in" is discouraged, but if the original is
 *   unavailable "both the original and the secondary source must be listed" —
 *   "Louis Zukofsky, 'Sincerity and Objectification,' Poetry 37 (February 1931): 269,
 *   quoted in Bonnie Costello, Marianne Moore: Imaginary Possessions (Harvard
 *   University Press, 1981), 78." The bibliography gives the original with a closing
 *   "Quoted in …" sentence, and the secondary source its own entry (the pattern the
 *   site's secondary-source guide documents).
 * - Harvard (Cite Them Right, 13th ed., 2025), as the Open University's guide gives
 *   it: "West et al. (2007, quoted in Birch, 2017, p. 17)" and "(Leach, 2015, cited in
 *   The Open University, 2022)" — "quoted in" for a quotation, "cited in" for a
 *   paraphrase; only the source you read is in the reference list. Cite Them Right
 *   joins two or three names with "and" and uses an italic "et al." from four.
 */

export const SECONDARY_SOURCE_FIELDS = {
  originalAuthors: "originalAuthors",
  originalYear: "originalYear",
  secondaryAuthors: "secondaryAuthors",
  secondaryYear: "secondaryYear",
  page: "page",
  use: "use",
  originalTitle: "originalTitle",
  originalKind: "originalKind",
  originalContainer: "originalContainer",
  originalDetails: "originalDetails",
  originalPage: "originalPage",
  secondaryTitle: "secondaryTitle",
  secondaryKind: "secondaryKind",
  secondaryContainer: "secondaryContainer",
  secondaryDetails: "secondaryDetails",
} as const;

const F = SECONDARY_SOURCE_FIELDS;

export interface SecondarySourceLabels {
  apa: string;
  mla: string;
  chicago: string;
  harvard: string;
  parenthetical: string;
  narrative: string;
  mlaAuthorInProse: string;
  mlaAuthorInParentheses: string;
  footnote: string;
  shortNote: string;
  bibliographyOriginal: string;
  bibliographySecondary: string;
  /** With {secondary}: the one work that goes in the list. */
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
  chicagoNeedsTitles: string;
  harvardNote: string;
}

interface Work {
  authors: PersonName[];
  year: string;
  title: string;
  /** "article" (quoted title, in a container) or "book" (italic title). */
  kind: string;
  container: string;
  details: string;
}

function readWork(
  values: SpecialValues,
  which: "original" | "secondary"
): Work {
  const original = which === "original";
  return {
    authors: parsePeople(
      get(values, original ? F.originalAuthors : F.secondaryAuthors)
    ),
    year: get(values, original ? F.originalYear : F.secondaryYear),
    title: get(values, original ? F.originalTitle : F.secondaryTitle),
    kind: get(values, original ? F.originalKind : F.secondaryKind) || "book",
    container: get(
      values,
      original ? F.originalContainer : F.secondaryContainer
    ),
    details: get(values, original ? F.originalDetails : F.secondaryDetails),
  };
}

export function secondarySourceIsReady(values: SpecialValues): boolean {
  return (
    get(values, F.originalAuthors) !== "" &&
    get(values, F.secondaryAuthors) !== ""
  );
}

/* ------------------------------------------------------------------- APA */

function apaOutput(
  original: Work,
  secondary: Work,
  page: string,
  labels: SecondarySourceLabels
): CitationOutput {
  const who = apaInTextAuthors(original.authors);
  const year = secondary.year || "n.d.";
  const pageText = page ? `, p. ${page}` : "";
  const cited = `as cited in ${apaInTextAuthors(secondary.authors)}, ${year}${pageText}`;
  // APA leaves out an unknown primary year rather than writing n.d.
  const parenthetical = original.year
    ? `(${who}, ${original.year}, ${cited})`
    : `(${who}, ${cited})`;
  const narrativeWho = apaInTextAuthors(original.authors, true);
  const narrative = original.year
    ? `${narrativeWho} (${original.year}, ${cited})`
    : `${narrativeWho} (${cited})`;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.parenthetical, parts: [plain(parenthetical)] },
      { label: labels.narrative, parts: [plain(narrative)] },
    ],
    note: t(labels.apaNote, {
      secondary: `${apaInTextAuthors(secondary.authors, true)} (${year})`,
    }),
  };
}

/* ------------------------------------------------------------------- MLA */

/** "Alexander Pope", "Smith and Lee", "Smith et al." — the original, named in full. */
function mlaOriginalName(names: PersonName[]): string {
  if (names.length >= 3) {
    return `${naturalName(names[0])} et al.`;
  }
  return names.map(naturalName).join(" and ");
}

function mlaOutput(
  original: Work,
  secondary: Work,
  page: string,
  labels: SecondarySourceLabels
): CitationOutput {
  const where = `qtd. in ${mlaInTextAuthors(secondary.authors)}${page ? ` ${page}` : ""}`;
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.mlaAuthorInProse, parts: [plain(`(${where})`)] },
      {
        label: labels.mlaAuthorInParentheses,
        parts: [plain(`(${mlaOriginalName(original.authors)} ${where})`)],
      },
    ],
    note: t(labels.mlaNote, { secondary: mlaInTextAuthors(secondary.authors) }),
  };
}

/* --------------------------------------------------------------- Chicago */

/** The title and publication facts of one work, in note form. */
function chicagoNoteWork(work: Work, page: string): CitationParts {
  const title = titleCase(work.title);
  const where = page ? `: ${page}` : "";
  if (work.kind === "article") {
    return [
      plain(`“${title},” `),
      italic(work.container),
      plain(`${work.details ? ` ${work.details}` : ""}${where}`),
    ];
  }
  const publisher = publisherName(work.details, "chicago");
  const facts = [publisher, work.year].filter(Boolean).join(", ");
  return [
    italic(title),
    plain(`${facts ? ` (${facts})` : ""}${page ? `, ${page}` : ""}`),
  ];
}

/** The short form: "Zukofsky, “Sincerity and Objectification,” 269". */
function chicagoShortWork(work: Work, page: string): CitationParts {
  const short = titleCase(mainTitle(work.title));
  const who = `${chicagoShortAuthors(work.authors)}, `;
  const at = page ? `, ${page}` : "";
  if (work.kind === "article") {
    return [plain(`${who}“${short},”${page ? ` ${page}` : ""}`)];
  }
  return [plain(who), italic(short), plain(at)];
}

/** The work as a bibliography entry, without the closing period. */
function chicagoBibliographyWork(work: Work, page: string): CitationParts {
  const who = chicagoBibliographyAuthors(work.authors);
  const title = titleCase(work.title);
  if (work.kind === "article") {
    return [
      plain(`${who}${closer(who)} “${title}${closer(title)}” `),
      italic(work.container),
      plain(
        `${work.details ? ` ${work.details}` : ""}${page ? `: ${page}` : ""}`
      ),
    ];
  }
  const publisher = publisherName(work.details, "chicago");
  const facts = [publisher, work.year].filter(Boolean).join(", ");
  return [
    plain(`${who}${closer(who)} `),
    italic(title),
    plain(`${closer(title)}${facts ? ` ${facts}` : ""}`),
  ];
}

function chicagoOutput(
  original: Work,
  secondary: Work,
  pages: { original: string; secondary: string },
  labels: SecondarySourceLabels
): CitationOutput {
  if (original.title === "" || secondary.title === "") {
    return {
      key: "chicago",
      label: labels.chicago,
      entries: [],
      note: labels.chicagoNeedsTitles,
    };
  }
  const originalNote = chicagoNoteAuthors(original.authors);
  const secondaryNote = chicagoNoteAuthors(secondary.authors);
  const note: CitationParts = [
    plain(`${originalNote}, `),
    ...chicagoNoteWork(original, pages.original),
    plain(`, quoted in ${secondaryNote}, `),
    ...chicagoNoteWork(secondary, pages.secondary),
    plain("."),
  ];
  const shortNote: CitationParts = [
    ...chicagoShortWork(original, pages.original),
    plain(", quoted in "),
    ...chicagoShortWork(secondary, pages.secondary),
    plain("."),
  ];
  const originalEntry: CitationParts = [
    ...chicagoBibliographyWork(original, pages.original),
    plain(`. Quoted in ${secondaryNote}, `),
    ...chicagoNoteWork(secondary, pages.secondary),
    plain("."),
  ];
  const secondaryEntry: CitationParts = [
    ...chicagoBibliographyWork(secondary, ""),
    plain("."),
  ];
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.footnote, parts: tidyParts(note) },
      { label: labels.shortNote, parts: tidyParts(shortNote) },
      { label: labels.bibliographyOriginal, parts: tidyParts(originalEntry) },
      { label: labels.bibliographySecondary, parts: tidyParts(secondaryEntry) },
    ],
    note: labels.chicagoNote,
  };
}

/* --------------------------------------------- Harvard (Cite Them Right) */

/** Cite Them Right: "Smith", "Smith and Lee", "Smith, Lee and Kahn", "Smith *et al.*". */
function harvardNames(names: PersonName[]): CitationParts {
  const surnames = names.map(familyName);
  if (surnames.length >= 4) {
    return [plain(`${surnames[0]} `), italic("et al.")];
  }
  if (surnames.length <= 2) {
    return [plain(surnames.join(" and "))];
  }
  return [plain(`${surnames.slice(0, -1).join(", ")} and ${surnames.at(-1)}`)];
}

function harvardOutput(
  original: Work,
  secondary: Work,
  page: string,
  quoting: boolean,
  labels: SecondarySourceLabels
): CitationOutput {
  const link = quoting ? "quoted in" : "cited in";
  const year = secondary.year || "no date";
  const tail: CitationParts = [
    plain(`${link} `),
    ...harvardNames(secondary.authors),
    plain(`, ${year}${page ? `, p. ${page}` : ""})`),
  ];
  const originalYear = original.year ? `${original.year}, ` : "";
  return {
    key: "harvard",
    label: labels.harvard,
    entries: [
      {
        label: labels.parenthetical,
        parts: tidyParts([
          plain("("),
          ...harvardNames(original.authors),
          plain(`, ${originalYear}`),
          ...tail,
        ]),
      },
      {
        label: labels.narrative,
        parts: tidyParts([
          ...harvardNames(original.authors),
          plain(` (${originalYear}`),
          ...tail,
        ]),
      },
    ],
    note: labels.harvardNote,
  };
}

export function formatSecondarySource(
  values: SpecialValues,
  labels: SecondarySourceLabels
): CitationOutput[] {
  const original = readWork(values, "original");
  const secondary = readWork(values, "secondary");
  const page = get(values, F.page);
  const quoting = get(values, F.use) !== "paraphrase";
  return [
    apaOutput(original, secondary, page, labels),
    mlaOutput(original, secondary, page, labels),
    chicagoOutput(
      original,
      secondary,
      { original: get(values, F.originalPage), secondary: page },
      labels
    ),
    harvardOutput(original, secondary, page, quoting, labels),
  ];
}
